import {Injectable} from '@angular/core';
import {environment} from './../../../environments/environment';
import {Router} from '@angular/router';
import {JwtParserService} from './jwt-parser.service';
import {ParamService} from './param.service';
import {Location} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';
import {v4 as uuidv4} from 'uuid';

@Injectable({
    providedIn: 'root',
})
export class AuthService {

    banner = '';
    isUserAuthenticated = false;
    checkUserHasActiveSession = false;
    populateSessionData$ = new BehaviorSubject(null);
    rewardsDetailSessionData$ = new BehaviorSubject(null);
    getYourRewardsDataSessionData$ = new BehaviorSubject(null);
    pointsHistorySessionData$ = new BehaviorSubject(null);
    rewardPartnerSessionData$ = new BehaviorSubject(null);
    rewardPartnerDetailSessionData$ = new BehaviorSubject(null);
    rewardPartnerHubSessionData$ = new BehaviorSubject(null);
    REWARDS_DETAILS_SESSION_CALLER = 'rewards-detail';
    MANAGE_REWARDS_SESSION_CALLER = 'manage-rewards';
    GET_YOUR_REWARDS_SESSION_CALLER = 'get-your-rewards';
    POINTS_HISTORY_SESSION_CALLER = 'points-history';
    REWARDS_PARTNER_SESSION_CALLER = 'rewards-partner';
    REWARDS_PARTNER_DETAIL_SESSION_CALLER = 'partner-detail';
    REWARDS_PARTNER_HUB_SESSION_CALLER = 'partner-hub';
    userInfo: any;
    _window: any = window;

    constructor(private router: Router,
                private location: Location,
                private jwtParserService: JwtParserService,
                private paramService: ParamService,
                private http: HttpClient) {
        this.banner = this.paramService.banner;
    }

    deleteCookie = (name, domain) => document.cookie = `${name}=; expires=Thu, 01-Jan-1970 00:00:01 GMT; path=/`;

    setCookie = (name, value) => document.cookie = `${name}=${value}; path=/`;

    isLocal() {
        if (window.location.href.indexOf('local') > 0) {
            return true;
        } else {
            return false;
        }
    }

    assignUrl(url) {
        window.location.assign(url);
    }

    async getUserInfo() {
        // clear any existing cookies
        this.clearEcomCookies();

        let url;
        if (this.isLocal()) {
            url = 'https://www-qa1.' + this.banner + '.com/bin/safeway/unified/userinfo?banner=' + this.banner;
        } else {
            url = 'https://' + window.location.host + '/bin/safeway/unified/userinfo?banner=' + this.banner;
        }
        await this.http.get(`${url}&rand=${uuidv4()}`, {withCredentials: true}).subscribe((response: any) => {
                if (response && response['SWY_SHOP_TOKEN']) {
                    if (response.userType !== 'R') {
                        this.checkTokenValid(response);
                    } else {
                        localStorage.setItem('referralUrl', document.referrer);
                        this.redirectToSSO(response);
                    }
                } else {
                    localStorage.setItem('referralUrl', document.referrer);
                    this.redirectToSSO(response);
                }
            },
            error => {
                this.redirectToSSO(null);
            });

        return this.isUserAuthenticated;
    }

    async getUserLogedInToken(tokenCaller) {
        // clear any existing cookies
        let url;
        let isUserAuthenticated;
        if (this.isLocal()) {
            url = 'https://www-qa1.' + this.banner + '.com/bin/safeway/unified/userinfo?banner=' + this.banner;
        } else {
            url = 'https://' + window.location.host + '/bin/safeway/unified/userinfo?banner=' + this.banner;
        }
        this.http.get(`${url}&rand=${uuidv4()}`, {withCredentials: true}).subscribe((response: any) => {
                if (response && response['SWY_SHOP_TOKEN']) {
                    isUserAuthenticated = this.checkUserTokenValidity(response);
                    this.userInfo = {...(response), isTokenValid: isUserAuthenticated};
                } else {
                    isUserAuthenticated = false;
                    this.userInfo = {...(response), isTokenValid: isUserAuthenticated};
                }
                switch (tokenCaller) {
                    case this.REWARDS_DETAILS_SESSION_CALLER:
                        this.rewardsDetailSessionData$.next(this.userInfo);
                        break;
                    case this.MANAGE_REWARDS_SESSION_CALLER:
                        this.populateSessionData$.next({...(response), isTokenValid: isUserAuthenticated});
                        break;
                    case this.GET_YOUR_REWARDS_SESSION_CALLER:
                        this.getYourRewardsDataSessionData$.next({...(response), isTokenValid: isUserAuthenticated});
                        break;
                    case this.POINTS_HISTORY_SESSION_CALLER:
                        this.pointsHistorySessionData$.next({...(response), isTokenValid: isUserAuthenticated});
                        break;
                    case this.REWARDS_PARTNER_SESSION_CALLER:
                        this.rewardPartnerSessionData$.next({...(response), isTokenValid: isUserAuthenticated});
                        break;
                    case this.REWARDS_PARTNER_DETAIL_SESSION_CALLER:
                        this.rewardPartnerDetailSessionData$.next({...(response), isTokenValid: isUserAuthenticated});
                        break;
                    case this.REWARDS_PARTNER_HUB_SESSION_CALLER:
                        this.rewardPartnerHubSessionData$.next({...(response), isTokenValid: isUserAuthenticated});
                        break;

                    default:
                        this.populateSessionData$.next(this.userInfo);
                        break;
                }
            },
            error => {
                isUserAuthenticated = false;
            });
        return isUserAuthenticated;
    }

    checkTokenValid(user) {
        const currentDate = new Date();
        const expiryDate = new Date(Number(user['expireTime']));
        if (user && user['SWY_SHOP_TOKEN'] && (expiryDate > currentDate)) {
            this.isUserAuthenticated = true;
            this.checkUserHasActiveSession = true;
        } else {
            this.isUserAuthenticated = false;
        }
    }

    checkUserTokenValidity(user) {
        let isTokenValid = false;
        const currentDate = new Date();
        const expiryDate = new Date(Number(user['expireTime']));
        if (user && user['SWY_SHOP_TOKEN'] && (expiryDate > currentDate)) {
            isTokenValid = true;
        } else {
            isTokenValid = false;
        }
        return isTokenValid;
    }

    isTokenValidForSession(user) {
        const currentDate = new Date();
        const expiryDate = new Date(Number(user['expireTime']));
        if (user && user['SWY_SHOP_TOKEN'] && (expiryDate > currentDate)) {
            this.isUserAuthenticated = true;
        } else {
            this.isUserAuthenticated = false;
        }
        return this.isUserAuthenticated;
    }

    isUserLoggedIn(user) {
        const currentDate = new Date();
        const expiryDate = new Date(Number(user['expireTime']));
        if (user && user['SWY_SHOP_TOKEN'] && (expiryDate > currentDate)) {
            this.isUserAuthenticated = true;
            this.checkUserHasActiveSession = true;
        } else {
            this.isUserAuthenticated = false;
        }
        return this.isUserAuthenticated;
    }

    redirectToSSO(user) {
        const sso_url = this.getSSORoute(user);
        this.setEcomRedirectURLCookie();
        this.assignUrl(sso_url);
    }

    setEcomRedirectURLCookie() {
        let isB2B = this.checkB2B();
        let cookieURL;
        if (isB2B) {
            cookieURL = 'ACI_S_B_ECommRedirectURL';
        } else {
            cookieURL = 'ACI_S_ECommRedirectURL';
        }
        this.clearForceSignin();
        this.setCookie(cookieURL, encodeURIComponent(window.location.href));
    }

    clearForceSignin() {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        if (params.has('forceSignin') && params.get('forceSignin') === 'true') {
            params.delete('forceSignin');
            window.history.replaceState({}, '', url.toString());
        }
    }

    public checkB2B() {
        const b2bEle = document.querySelector('#b2b');
        if (b2bEle && b2bEle.getAttribute('data-b2b').toLowerCase() === 'true') {
            return true
        }
        return false
    }

    getApplicationDomainURL() {
        let domain;
        if (this.isLocal()) {
            domain = 'https://www-qa1.' + this.banner + '.com';
        } else {
            domain = 'https://' + window.location.host;
        }
        return domain;
    }

    getSSORoute(user) {
        let sso_url;
        if (user && user['userType'] === 'G') {
            sso_url = this.getApplicationDomainURL() + environment.SIGN_IN_URL;
            sso_url = sso_url.replace(/\{banner\}/g, this.banner);
        } else if (user && user['userType'] === 'R') {
            sso_url = this.getApplicationDomainURL() + environment.RE_SIGN_IN_URL;
            sso_url = sso_url.replace(/\{banner\}/g, this.banner);
        } else {
            if (window['AB'] && window['AB']['COMMON'] && window['AB']['COMMON'].getCookie('SAFEWAY_KMSI') === true) {
                sso_url = this.getApplicationDomainURL() + environment.RE_SIGN_IN_URL;
                sso_url = sso_url.replace(/\{banner\}/g, this.banner);
            } else {
                sso_url = this.getApplicationDomainURL() + environment.SIGN_IN_URL;
                sso_url = sso_url.replace(/\{banner\}/g, this.banner);
            }
        }
        return sso_url;
    }

    clearEcomCookies() {
        let cookies = ['ACI_S_ECommRedirectURL', 'ACI_S_B_ECommRedirectURL']
        cookies.forEach(cookieURL => {
            this.deleteCookie(cookieURL, '');
        })
    }

    redirectToHome() {

        const url = window['SWY'].CONFIGSERVICE.bannerDomainMap['www-' + this.banner];
        this.setEcomRedirectURLCookie()
        this.assignUrl(url);
    }

    parseJwt(jwtToken) {
        const base64Url = jwtToken.split('.')[1];
        const base64 = base64Url.replace('-', '+').replace('_', '/');
        return JSON.parse(window.atob(base64));
    }

    public updateUserInfo(userInfo: any) {
        const oktaToken = this.parseJwt(userInfo.SWY_SHOP_TOKEN);
        this._window["AB"].userInfo.SWY_SHOP_TOKEN = userInfo.SWY_SHOP_TOKEN;
        this._window["AB"].userInfo.email = userInfo.email;
        this._window["AB"].userInfo.banner = userInfo.banner || this.banner;
        this._window["AB"].userInfo.j4u.zipcode = userInfo.j4uZipcode || userInfo.zipcode;
        this._window["AB"].userInfo.clubCard = userInfo.clubCardNumber;
        this._window["AB"].userInfo.j4u.storeId = userInfo.j4uStoreId || userInfo.storeId;
        this._window["AB"].userInfo.firstName = userInfo.firstName;
        this._window["AB"].userInfo.displayableBanner = userInfo.displayableBanner;
        this._window["AB"].userInfo.pickupAddress = userInfo.pickupAddress;
        this._window["AB"].userInfo.customerId = userInfo.customerId;
        this._window["AB"].userInfo.userType = userInfo.userType;
        this._window["AB"].userInfo.UUID = userInfo.UUID;
        this._window["AB"].userInfo.branchId = userInfo.j4uStoreId || userInfo.storeId;
        this._window["AB"].userInfo.expireTime = userInfo.expireTime;
        this._window["AB"].userInfo.houseId  = oktaToken.hid;
        this._window["AB"].userInfo.siteType = userInfo.siteType;
    }
}
